<template>
  <div ref="commonStreamRef" class="!mt-[6px]">
    <div
      v-if="isLoading"
      class="h-[55px] flex items-center gap-2 animate-pulse"
    >
      <div
        class="w-[40px] h-[40px] bg-gray-200 dark:bg-gray-700 rounded-full shrink-0"
      ></div>

      <div class="flex space-x-[3px] h-full overflow-hidden items-end">
        <div
          v-for="(bar, index) in bars"
          :key="index"
          class="w-[6px] bg-gray-200 dark:bg-gray-700 rounded loader animate-wave"
          :style="{ height: `${bar}%` }"
        ></div>
      </div>
    </div>

    <div v-show="!isLoading" class="audio-player relative">
      <div
        class="bg-blue-500 text-white w-[40px] h-[40px] absolute top-1.5 left-0 rounded-full flex items-center justify-center"
        @click="toggleMute"
      >
        <fai :icon="isMuted ? 'volume-xmark' : 'volume-high'" />
      </div>
      <!-- Ses Kapalı/Açık Butonu -->

      <!-- Ses Oynatıcı -->
      <audio
        ref="audio"
        @loadedmetadata="initializeRecordedAudio"
        @error="handleError"
        crossorigin="anonymous"
      ></audio>

      <!-- Ses Görselleştirme -->
      <canvas v-if="!audioEnded" ref="visualizer" height="55"></canvas>

      <div v-else class="h-[55.3px]"></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, inject, onBeforeUnmount, watch, nextTick } from "vue"
  import { useWebSocket } from "@/lib/useWebSocket"
  import { useWakeLock } from "@vueuse/core"
  const { isSupported, isActive, request, release } = useWakeLock()

  const toggleWakeLock = async () => {
    if (isActive.value) {
      // await release()
    } else {
      try {
        await request()
      } catch (error) {
        console.error("Wake Lock hatası:", error)
      }
    }
  }

  const {
    connect,
    analyser,
    audioEnded,
    initAudioContext,
    isLoading,
    setMute,
    isMuted,
  } = useWebSocket()

  const props = defineProps({
    link: { type: String, default: "" },
    delay: { type: Number },
    message: { type: Object },
  })

  const bars = ref([])

  const audio = ref(null)
  const visualizer = ref(null)

  const commonStreamRef = ref(null)

  // Burada iki ayrı mod var: canlı mı, kayıt mı?
  const isStreaming = ref(true) // true ise canlı WebSocket, false ise recorded

  const hasAudio = inject("hasAudio")
  const streamCounter = inject("streamCounter")

  // Kayıttan çalma için ayrı context ve analyser referansları:
  const recordedAudioContext = ref(null)
  const recordedAnalyser = ref(null)
  const recordedSourceNode = ref(null)

  // Görselleştirme için kullanılan animationFrame
  const animationId = ref(null)

  const toggleMute = () => {
    setMute(!isMuted.value)
  }

  // ---------------- LİFECYCLE -------------------
  onMounted(async () => {
    await nextTick() // DOM render'ını bekle
    const boxWidth = commonStreamRef.value?.offsetWidth || 0
    const barWidth = 6
    const gap = 3
    const totalBarUnit = barWidth + gap
    const barCount = Math.floor(boxWidth / totalBarUnit)

    bars.value = Array.from(
      { length: barCount },
      () => Math.floor(Math.random() * (100 - 20 + 1)) + 20
    )

    props.message.messageWriter = false
    initAudioContext()
    connect(props.link)

    // Websocket stream başladığında hemen viz başlat
    const interval = setInterval(() => {
      if (analyser.value) {
        startVisualization()
        clearInterval(interval)
      }
    }, 100)
  })

  // WebSocket tamamlanınca recordedAudioUrl oluşuyor

  watch(audioEnded, (newVal) => {
    if (newVal) {
      stopVisualization()
      streamCounter.value++
    }
  })

  // Kayıttan ses açıldığında (audio yüklenince) analyser bağla:
  const initializeRecordedAudio = () => {
    if (isStreaming.value) return // canlı için çalışmasın
    if (!audio.value) return
    if (recordedAudioContext.value) return // ikincisini kurma

    const audioElement = audio.value
    recordedAudioContext.value = new (window.AudioContext ||
      window.webkitAudioContext)()
    recordedSourceNode.value =
      recordedAudioContext.value.createMediaElementSource(audioElement)
    recordedAnalyser.value = recordedAudioContext.value.createAnalyser()
    recordedAnalyser.value.fftSize = 256
    recordedSourceNode.value.connect(recordedAnalyser.value)
    recordedAnalyser.value.connect(recordedAudioContext.value.destination)
    // Oynatıldığında recorded viz başlat
    startVisualization()
  }

  // ========== VİZUALİZASYON KISMI ===========
  const startVisualization = () => {
    const canvas = visualizer.value
    let usedAnalyser = isStreaming.value
      ? analyser.value
      : recordedAnalyser.value
    if (!canvas || !usedAnalyser) return

    const canvasCtx = canvas.getContext("2d")
    const bufferLength = usedAnalyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const draw = () => {
      toggleWakeLock()
      animationId.value = requestAnimationFrame(draw)
      usedAnalyser.getByteFrequencyData(dataArray)
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height)

      const barWidth = commonStreamRef.value.width
        ? commonStreamRef.value.width
        : canvas.width / bufferLength
      let barHeight
      let x = 0
      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i]
        canvasCtx.fillStyle = "rgb(59, 130, 246)" // Mavi renk
        canvasCtx.fillRect(
          x,
          canvas.height - barHeight / 2,
          barWidth,
          barHeight / 2
        )
        x += barWidth + 1
      }
    }
    draw()
  }

  const stopVisualization = () => {
    if (animationId.value) {
      cancelAnimationFrame(animationId.value)
      animationId.value = null
    }
  }

  const handleError = () => {
    console.error("Audio loading error!")
  }

  onBeforeUnmount(() => {
    stopVisualization()
    if (recordedAudioContext.value) recordedAudioContext.value.close()
  })
</script>

<style scoped>
  .audio-player {
    display: flex;
    align-items: center;
    flex-direction: column;
  }
  canvas {
    width: 70%;
    height: 55px;
  }
</style>
